import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Clock,
  Calendar,
  FileText,
  Check,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Send,
  Download,
  Mic,
  MicOff,
  Loader2,
  CheckCircle2,
  Edit3,
  User,
  MapPin
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { SignaturePad } from '../../components/ui/signature-pad';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Caregiver Timesheet & Care Notes View
 * 
 * Features:
 * - View current pay period timesheet
 * - See all entries with hours
 * - Enter daily care notes (type or voice)
 * - Submit timesheet with electronic signature
 * - Download PDF
 */

export default function CaregiverTimesheet() {
  const navigate = useNavigate();
  const { timesheetId } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [careNotes, setCareNotes] = useState([]);
  const [payPeriod, setPayPeriod] = useState(null);
  
  // Care note editing
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Signature modal
  const [showSignature, setShowSignature] = useState(false);
  const [signingType, setSigningType] = useState(null); // 'timesheet' or 'care_note'
  const [signingId, setSigningId] = useState(null);

  useEffect(() => {
    fetchPayPeriod();
    fetchTimesheets();
  }, []);

  useEffect(() => {
    if (timesheetId) {
      fetchTimesheetDetails(timesheetId);
    }
  }, [timesheetId]);

  const fetchPayPeriod = async () => {
    try {
      const res = await fetch(`${API_URL}/api/timesheets/pay-periods/current`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPayPeriod(data);
      }
    } catch (err) {
      console.error('Error fetching pay period:', err);
    }
  };

  const fetchTimesheets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/timesheets/my-timesheets?limit=10`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTimesheets(data.items || []);
        
        // Auto-select first draft timesheet or most recent
        if (data.items?.length > 0 && !timesheetId) {
          const draft = data.items.find(t => t.status === 'draft');
          if (draft) {
            fetchTimesheetDetails(draft.id);
          } else {
            fetchTimesheetDetails(data.items[0].id);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching timesheets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/timesheets/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTimesheet(data.timesheet);
        setCareNotes(data.care_notes || []);
      }
    } catch (err) {
      console.error('Error fetching timesheet details:', err);
    }
  };

  // Voice recording for care notes
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const ext = mimeType.split('/')[1].replace('mpeg', 'mp3');
        await transcribeAudio(audioBlob, ext);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob, ext) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording.${ext}`);
      formData.append('field_name', 'care_notes');
      
      const res = await fetch(`${API_URL}/api/data-entry/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      
      if (res.ok) {
        const result = await res.json();
        setNoteText(prev => prev + (prev ? ' ' : '') + result.processed_value);
        toast.success('Voice transcribed');
      } else {
        toast.error('Transcription failed');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      toast.error('Transcription failed');
    } finally {
      setTranscribing(false);
    }
  };

  const saveCareNote = async () => {
    if (!editingNote || !noteText.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/timesheets/care-notes/${editingNote.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: noteText,
          entry_method: 'typed'
        })
      });
      
      if (res.ok) {
        toast.success('Care note saved');
        setEditingNote(null);
        setNoteText('');
        fetchTimesheetDetails(selectedTimesheet.id);
      }
    } catch (err) {
      console.error('Error saving care note:', err);
      toast.error('Failed to save');
    }
  };

  const openCareNoteForSigning = (note) => {
    setSigningType('care_note');
    setSigningId(note.id);
    setNoteText(note.notes || '');
    setShowSignature(true);
  };

  const submitCareNote = async (signatureData) => {
    try {
      const res = await fetch(`${API_URL}/api/timesheets/care-notes/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          care_note_id: signingId,
          notes: noteText,
          signature: signatureData
        })
      });
      
      if (res.ok) {
        toast.success('Care note submitted');
        setShowSignature(false);
        setSigningId(null);
        setNoteText('');
        fetchTimesheetDetails(selectedTimesheet.id);
      }
    } catch (err) {
      console.error('Error submitting care note:', err);
      toast.error('Failed to submit');
    }
  };

  const submitTimesheet = async (signatureData) => {
    try {
      const res = await fetch(`${API_URL}/api/timesheets/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          timesheet_id: selectedTimesheet.id,
          signature: signatureData
        })
      });
      
      if (res.ok) {
        toast.success('Timesheet submitted! Office staff will review.');
        setShowSignature(false);
        fetchTimesheetDetails(selectedTimesheet.id);
        fetchTimesheets();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit');
      }
    } catch (err) {
      console.error('Error submitting timesheet:', err);
      toast.error('Failed to submit');
    }
  };

  const downloadPdf = async () => {
    if (!selectedTimesheet) return;
    
    try {
      window.open(`${API_URL}/api/timesheets/${selectedTimesheet.id}/pdf`, '_blank');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#176DFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA] pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0D1B33] to-[#1a2d4d] text-white px-4 py-6">
        <button
          onClick={() => navigate('/my-leynix')}
          className="flex items-center gap-2 text-white/70 hover:text-white mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
        <h1 className="text-xl font-bold mb-1">My Timesheets</h1>
        {payPeriod && (
          <p className="text-white/70 text-sm">
            Current period: {payPeriod.start} to {payPeriod.end}
            {payPeriod.is_due_soon && (
              <span className="ml-2 bg-amber-500 text-white px-2 py-0.5 rounded text-xs">
                Due in {payPeriod.days_until_due} days
              </span>
            )}
          </p>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Timesheet selector */}
        {timesheets.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {timesheets.map((ts) => (
              <button
                key={ts.id}
                onClick={() => fetchTimesheetDetails(ts.id)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedTimesheet?.id === ts.id
                    ? 'bg-[#176DFF] text-white'
                    : 'bg-white border border-gray-200 text-gray-700'
                }`}
              >
                {ts.client_name}
                <span className="ml-2 text-xs opacity-70">
                  {ts.status === 'draft' ? '(Draft)' : ts.status === 'submitted' ? '(Submitted)' : ''}
                </span>
              </button>
            ))}
          </div>
        )}

        {selectedTimesheet ? (
          <>
            {/* Timesheet summary card */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-[#176DFF]" />
                  <span className="font-semibold text-[#0D1B33]">{selectedTimesheet.client_name}</span>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  selectedTimesheet.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                  selectedTimesheet.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                  selectedTimesheet.status === 'approved' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {selectedTimesheet.status.charAt(0).toUpperCase() + selectedTimesheet.status.slice(1)}
                </span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <MapPin className="w-4 h-4" />
                {selectedTimesheet.client_address}
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-[#176DFF]">{selectedTimesheet.total_hours}</p>
                  <p className="text-xs text-gray-500">Total Hours</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-[#0D1B33]">{selectedTimesheet.total_entries}</p>
                  <p className="text-xs text-gray-500">Visits</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-emerald-600">
                    {selectedTimesheet.care_notes_completed}/{selectedTimesheet.care_notes_required}
                  </p>
                  <p className="text-xs text-gray-500">Notes Done</p>
                </div>
              </div>
            </div>

            {/* Care notes warning */}
            {!selectedTimesheet.all_care_notes_complete && selectedTimesheet.status === 'draft' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Care Notes Required</p>
                  <p className="text-sm text-amber-700">
                    Complete all care notes before submitting your timesheet.
                  </p>
                </div>
              </div>
            )}

            {/* Time entries */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-[#0D1B33]">Time Entries</h3>
                {selectedTimesheet.status !== 'draft' && (
                  <button
                    onClick={downloadPdf}
                    className="flex items-center gap-1 text-sm text-[#176DFF]"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                )}
              </div>
              
              <div className="divide-y divide-gray-100">
                {selectedTimesheet.entries?.map((entry, idx) => {
                  const careNote = careNotes.find(cn => cn.service_date === entry.service_date);
                  const noteComplete = careNote?.status === 'submitted';
                  
                  return (
                    <div key={entry.id || idx} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-[#0D1B33]">{entry.service_date}</span>
                        </div>
                        <span className="text-sm font-semibold text-[#176DFF]">
                          {entry.hours_worked} hrs
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {entry.time_in} - {entry.time_out}
                        </span>
                      </div>
                      
                      {/* Care note status */}
                      <div className={`p-3 rounded-lg ${noteComplete ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {noteComplete ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <FileText className="w-4 h-4 text-gray-400" />
                            )}
                            <span className={`text-sm ${noteComplete ? 'text-emerald-700' : 'text-gray-600'}`}>
                              {noteComplete ? 'Care note submitted' : 'Care note required'}
                            </span>
                          </div>
                          
                          {careNote && !noteComplete && selectedTimesheet.status === 'draft' && (
                            <button
                              onClick={() => {
                                setEditingNote(careNote);
                                setNoteText(careNote.notes || '');
                              }}
                              className="text-sm text-[#176DFF] flex items-center gap-1"
                            >
                              <Edit3 className="w-3 h-3" />
                              {careNote.notes ? 'Edit' : 'Add'}
                            </button>
                          )}
                        </div>
                        
                        {careNote?.notes && (
                          <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                            {careNote.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {(!selectedTimesheet.entries || selectedTimesheet.entries.length === 0) && (
                  <div className="p-8 text-center text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No time entries yet</p>
                    <p className="text-sm">Clock in/out to add entries</p>
                  </div>
                )}
              </div>
            </div>

            {/* Submit button */}
            {selectedTimesheet.status === 'draft' && selectedTimesheet.entries?.length > 0 && (
              <Button
                onClick={() => {
                  if (!selectedTimesheet.all_care_notes_complete) {
                    toast.error('Complete all care notes first');
                    return;
                  }
                  setSigningType('timesheet');
                  setShowSignature(true);
                }}
                className={`w-full py-4 text-base ${
                  selectedTimesheet.all_care_notes_complete
                    ? 'bg-[#176DFF] hover:bg-[#176DFF]/90'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
                disabled={!selectedTimesheet.all_care_notes_complete}
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Timesheet & Care Notes
              </Button>
            )}

            {selectedTimesheet.status === 'submitted' && (
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <CheckCircle2 className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="font-medium text-blue-900">Timesheet Submitted</p>
                <p className="text-sm text-blue-700">Waiting for office review</p>
              </div>
            )}

            {selectedTimesheet.status === 'approved' && (
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="font-medium text-emerald-900">Approved</p>
                <p className="text-sm text-emerald-700">Processing for payroll</p>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No timesheets yet</p>
            <p className="text-sm text-gray-400">
              Timesheets are created when you clock in for a visit
            </p>
          </div>
        )}
      </div>

      {/* Care Note Editor Modal */}
      {editingNote && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold text-[#0D1B33]">Care Note</h3>
              <button 
                onClick={() => { setEditingNote(null); setNoteText(''); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="w-5 h-5 text-gray-500 rotate-90" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-1">
                  {editingNote.service_date} • {editingNote.client_name}
                </p>
              </div>
              
              {/* Voice input */}
              <div className="flex justify-center mb-4">
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={transcribing}
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                    isRecording 
                      ? 'bg-red-500 animate-pulse' 
                      : 'bg-[#176DFF] hover:bg-[#176DFF]/90'
                  } ${transcribing ? 'opacity-50' : ''}`}
                >
                  {transcribing ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-6 h-6 text-white" />
                  ) : (
                    <Mic className="w-6 h-6 text-white" />
                  )}
                </button>
              </div>
              <p className="text-center text-sm text-gray-500 mb-4">
                {transcribing ? 'Transcribing...' : isRecording ? 'Tap to stop' : 'Tap to speak your notes'}
              </p>
              
              {/* Text input */}
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter care notes for this visit..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                rows={6}
              />
              
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={saveCareNote}
                  disabled={!noteText.trim()}
                >
                  Save Draft
                </Button>
                <Button
                  className="flex-1 bg-[#176DFF] hover:bg-[#176DFF]/90"
                  onClick={() => openCareNoteForSigning(editingNote)}
                  disabled={!noteText.trim()}
                >
                  Sign & Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignature && (
        <SignaturePad
          signerName={user?.name}
          acknowledgmentText={
            signingType === 'timesheet'
              ? "I certify that the hours and services documented on this timesheet are accurate and complete. I understand that falsification may result in disciplinary action."
              : "I certify that the care notes documented are accurate and reflect the services provided."
          }
          submitLabel={signingType === 'timesheet' ? 'Sign & Submit Timesheet' : 'Sign & Submit'}
          onSign={(sig) => {
            if (signingType === 'timesheet') {
              submitTimesheet(sig);
            } else {
              submitCareNote(sig);
            }
          }}
          onCancel={() => {
            setShowSignature(false);
            setSigningId(null);
            setSigningType(null);
          }}
        />
      )}
    </div>
  );
}
